import type { RefCallback } from "react";
import type { Control, FieldErrors } from "react-hook-form";
import type { TFunction } from "i18next";
import {
  type LmsCourseEditorFormValues,
  type LmsCourseLanguageVariant,
} from "@/components/lms/lms.utils";
import LmsCourseVariantCard from "@/components/lms/lmsCourseVariantCard";
import styles from "@/routes/lms/lmsCourseOverview.module.css";

interface Props {
  canEditVariants: boolean;
  canUploadFeaturedImage: boolean;
  canUploadScorm: boolean;
  control: Control<LmsCourseEditorFormValues>;
  courseIsActive: boolean;
  disabled: boolean;
  errors: FieldErrors<LmsCourseEditorFormValues>;
  onDownloadFeaturedImage: (variant: LmsCourseLanguageVariant) => void;
  onDownloadScorm: (variant: LmsCourseLanguageVariant) => void;
  onPreviewScorm?: (variant: LmsCourseLanguageVariant) => void;
  onUploadFeaturedImage: (variant: LmsCourseLanguageVariant) => void;
  onUploadScorm: (variant: LmsCourseLanguageVariant) => void;
  onViewFeaturedImage: (variant: LmsCourseLanguageVariant) => void;
  registerValidationTarget: (
    fieldName:
      | `variants.${number}.durationInMinutes`
      | `variants.${number}.isActive`
  ) => RefCallback<HTMLElement>;
  t: TFunction;
  variants: LmsCourseLanguageVariant[];
}

export default function LmsCourseVariantsSection({
  canEditVariants,
  canUploadFeaturedImage,
  canUploadScorm,
  control,
  courseIsActive,
  disabled,
  errors,
  onDownloadFeaturedImage,
  onDownloadScorm,
  onPreviewScorm,
  onUploadFeaturedImage,
  onUploadScorm,
  onViewFeaturedImage,
  registerValidationTarget,
  t,
  variants,
}: Props) {
  return (
    <section className={styles.variantsSection}>
      <div className={styles.settingsSectionHeader}>
        <h2>{t("course_language_variants")}</h2>
      </div>

      <div className={styles.variantCardsGrid}>
        {variants.map((variant, variantIndex) => (
          <LmsCourseVariantCard
            key={variant.id}
            canEditVariant={canEditVariants}
            canUploadFeaturedImage={canUploadFeaturedImage}
            canUploadScorm={canUploadScorm}
            control={control}
            courseIsActive={courseIsActive}
            disabled={disabled}
            errors={errors}
            onDownloadFeaturedImage={onDownloadFeaturedImage}
            onDownloadScorm={onDownloadScorm}
            onPreviewScorm={onPreviewScorm}
            onUploadFeaturedImage={onUploadFeaturedImage}
            onUploadScorm={onUploadScorm}
            onViewFeaturedImage={onViewFeaturedImage}
            registerValidationTarget={registerValidationTarget}
            t={t}
            variant={variant}
            variantIndex={variantIndex}
          />
        ))}
      </div>
    </section>
  );
}
